'use client';

import type { ReactNode } from 'react';
import { useBlockMap } from '@/lib/blockRegistry';
import { WIDGET_BY_TYPE } from '@/src/widgets/registry';
import type { BlockDto } from '@/types/blocks';

interface SlotRendererProps {
  blocks: BlockDto[];
  zoneName: string;
  /** Rendered inside the Hero wrapper — only used when zoneName="blocks" */
  headerSlot?: ReactNode;
  orientation?: 'vertical' | 'horizontal';
  /**
   * Whether this zone is a page's own top-level one, and therefore owes its
   * blocks the page container (70.14-Widget-Width).
   *
   * Defaults to `'none'`, so a zone rendered *inside* something that already
   * establishes a container — a column, a section, a collapsible body — needs
   * to pass nothing. Only `PageRenderer` and `LiveRenderTree` open a page's own
   * zone and ask for `'page'`; the other fifteen call sites inherit the default.
   *
   * Opt-in rather than opt-out because of that split: two sites to remember
   * instead of fifteen, and the two are stable entry points where a new layout
   * widget is added far more often than a new way of rendering a whole page.
   */
  container?: 'page' | 'none';
}

/** The one definition of a page's content column. */
const PAGE_CONTAINER = 'mx-auto w-full max-w-6xl px-4 lg:px-9';

/**
 * The container a block is wrapped in — conditional on both halves of the
 * convention.
 *
 * `container` answers "does this *zone* owe its blocks a container?", and
 * `layout: 'self'` answers "does this *widget* want one?". A layout widget
 * sitting in a page's top-level zone says no to the second, which is what keeps
 * it from getting the page container *and* establishing its own — the double
 * gutter this convention exists to prevent.
 */
function getWrapperClassName(
  block: BlockDto,
  container: 'page' | 'none',
): string {
  if (container === 'none') {
    return 'relative';
  }
  // Read from the registry rather than `useBlockMap()`, which maps to
  // components and drops the widget definition the declaration lives on.
  const widget = WIDGET_BY_TYPE.get(block.componentType);
  return widget?.layout === 'self' ? 'relative' : `relative ${PAGE_CONTAINER}`;
}

export function SlotRenderer({
  blocks,
  zoneName,
  headerSlot,
  orientation = 'vertical',
  container = 'none',
}: SlotRendererProps) {
  const blockMap = useBlockMap();

  return (
    <div
      data-strapi-zone-name={zoneName}
      data-strapi-zone-orientation={orientation}
    >
      {blocks.map((block, index) => {
        const Component = blockMap[block.componentType];
        if (!Component) {
          return null;
        }

        const wrapperClassName = getWrapperClassName(block, container);

        return (
          <div
            key={`${block.componentType}-${block.id}`}
            className={wrapperClassName || undefined}
            data-strapi-block-type={block.componentType}
            data-strapi-block-id={block.id}
            data-strapi-block-index={index}
          >
            {(block.componentType === 'widgets.hero' ||
              block.componentType === 'widgets.flexible-hero') &&
              headerSlot}
            <Component block={block} />
          </div>
        );
      })}
    </div>
  );
}
